/**
 * Set a release pin (D15, vendor.admin): the stage's or one tenant's site's
 * (site ids are unique only within a tenant), to one of the published
 * releases (newest first), with a note — a hold, a rollback, a support case. Only a published list can be chosen: the server refuses any
 * other, and the node's resolver would skip it.
 */
import type { VendorReleaseWire } from '@acp/api-contract/vendor.js';
import { type FormEvent, useState } from 'react';
import { type PinFormTarget, pinOf, shortHash } from '../../lib/vendor.js';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Field, FieldDescription, FieldError } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';

/** What is being pinned; `site` with an empty tenant or site id asks for it. */
export type PinDialogTarget = PinFormTarget;

export type PinDialogProps = {
  readonly target: PinDialogTarget | null;
  readonly releases: readonly VendorReleaseWire[];
  /** The list it is pinned to now, preselected; null for none. */
  readonly current: string | null;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (pin: { readonly target: PinDialogTarget; readonly listHash: string; readonly note: string }) => void;
};

/** A site pin's tenant and site id; a given one is fixed. */
function SiteFields({ target, tenantId, siteId, pending, onTenant, onSite }: {
  readonly target: { readonly tenantId: string; readonly siteId: string };
  readonly tenantId: string;
  readonly siteId: string;
  readonly pending: boolean;
  readonly onTenant: (value: string) => void;
  readonly onSite: (value: string) => void;
}) {
  return (
    <>
      <Field>
        <Label htmlFor="pin-tenant">Tenant id</Label>
        <Input id="pin-tenant" value={tenantId} onChange={(e) => onTenant(e.target.value)} disabled={pending || target.tenantId !== ''} />
        <FieldDescription>Site ids are unique only within a tenant.</FieldDescription>
      </Field>
      <Field>
        <Label htmlFor="pin-site">Site id</Label>
        <Input id="pin-site" value={siteId} onChange={(e) => onSite(e.target.value)} disabled={pending || target.siteId !== ''} />
      </Field>
    </>
  );
}

function PinForm({ target, releases, current, pending, error, onClose, onSubmit }: PinDialogProps & { readonly target: PinDialogTarget }) {
  const [listHash, setListHash] = useState(current ?? releases[0]?.listHash ?? '');
  const [tenantId, setTenantId] = useState(target.kind === 'site' ? target.tenantId : '');
  const [siteId, setSiteId] = useState(target.kind === 'site' ? target.siteId : '');
  const [note, setNote] = useState('');
  const pin = pinOf(target, tenantId, siteId, listHash);
  const ready = pin !== null;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (pin === null || pending) return;
    onSubmit({ target: pin, listHash, note: note.trim() });
  };
  return (
    <form className="grid gap-4" onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>{target.kind === 'stage' ? 'Pin the stage' : 'Pin a site'}</DialogTitle>
        <DialogDescription>
          {target.kind === 'stage'
            ? 'Every site without a site pin is served this release instead of the latest. Running productions keep their own package.'
            : 'This tenant’s site’s nodes are served this release whatever the stage pin says.'}
        </DialogDescription>
      </DialogHeader>
      {target.kind === 'site' ? <SiteFields target={target} tenantId={tenantId} siteId={siteId} pending={pending} onTenant={setTenantId} onSite={setSiteId} /> : null}
      <Field>
        <Label htmlFor="pin-release">Release</Label>
        <NativeSelect id="pin-release" value={listHash} onChange={(e) => setListHash(e.target.value)} disabled={pending || releases.length === 0}>
          {releases.map((r) => (
            <option key={r.listHash} value={r.listHash}>
              {shortHash(r.listHash)} · {r.commit}
              {r.publishedAt === null ? '' : ` · ${r.publishedAt.slice(0, 10)}`}
            </option>
          ))}
        </NativeSelect>
        {releases.length === 0 ? <FieldDescription>Nothing is published yet.</FieldDescription> : null}
      </Field>
      <Field>
        <Label htmlFor="pin-note">Note</Label>
        <Input id="pin-note" value={note} onChange={(e) => setNote(e.target.value)} disabled={pending} maxLength={512} placeholder="Hold: support case 1182" />
      </Field>
      <FieldError>{error}</FieldError>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!ready || pending} data-testid="pin-submit">
          {pending ? 'Pinning…' : 'Pin'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function PinDialog(props: PinDialogProps) {
  const { target } = props;
  return (
    <Dialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
    >
      <DialogContent data-testid="pin-dialog">{target === null ? null : <PinForm {...props} target={target} key={target.kind === 'stage' ? 'stage' : `site:${target.tenantId}/${target.siteId}`} />}</DialogContent>
    </Dialog>
  );
}
